import { useCallback, useEffect, useRef } from 'react'
import chatService from 'features/Chat/services/chatService'
import { socket } from 'features/Chat/ChatContext'
import type { IChatMessage } from 'features/Chat/Chat.types'

/**
 * Reports how far the reader has got in the open thread.
 *
 * Deliberately conservative. A naive implementation fires on every render and
 * every incoming message, and a group of eight people in a live conversation
 * turns into a write storm. This fires only when the thread is genuinely being
 * read, and never for a mark that has not advanced.
 */

const DEBOUNCE_MS = 800

export const useReadReceipts = ({
	eventId,
	userId,
	messages,
	isActive,
	isAtBottom,
	onReported,
}: {
	eventId: string
	userId: string
	messages: IChatMessage[]
	/** The popup is open AND the thread view is the one on screen. */
	isActive: boolean
	isAtBottom: boolean
	/** Lets the caller merge the mark locally without waiting for the socket. */
	onReported?: (lastReadMessageId: string, at: string) => void
}) => {
	const lastReportedId = useRef<string | null>(null)
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Reset when the thread changes: a mark from the previous thread says
	// nothing about this one.
	useEffect(() => {
		lastReportedId.current = null
	}, [eventId])

	const report = useCallback(async () => {
		const newest = messages?.[messages.length - 1]
		if (!eventId || !userId || !newest?._id) return
		// Nothing to report for a message this user sent themselves.
		if (newest.senderId?._id === userId) return
		if (lastReportedId.current === newest._id) return

		lastReportedId.current = newest._id
		const at = new Date().toISOString()

		try {
			await chatService().markThreadRead(eventId, newest._id)
		} catch (err) {
			// Let the next advance retry rather than blocking the UI on it.
			lastReportedId.current = null
			return
		}

		socket.emit('message_read', {
			roomId: eventId,
			eventId,
			userId,
			lastReadMessageId: newest._id,
			at,
		})
		onReported?.(newest._id, at)
	}, [eventId, userId, messages, onReported])

	useEffect(() => {
		if (!isActive || !isAtBottom) return
		if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return

		if (timer.current) clearTimeout(timer.current)
		timer.current = setTimeout(report, DEBOUNCE_MS)
		return () => {
			if (timer.current) clearTimeout(timer.current)
		}
	}, [isActive, isAtBottom, report])

	// A reader who tabs away and comes back has read whatever arrived meanwhile.
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === 'visible' && isActive && isAtBottom) report()
		}
		document.addEventListener('visibilitychange', onVisible)
		return () => document.removeEventListener('visibilitychange', onVisible)
	}, [isActive, isAtBottom, report])
}

export default useReadReceipts
